/**
 * Copyright 2025 Amazon.com, Inc. and its affiliates. All Rights Reserved.
 *
 * Licensed under the Amazon Software License (the "License").
 * You may not use this file except in compliance with the License.
 * A copy of the License is located at
 *
 *   http://aws.amazon.com/asl/
 *
 * or in the "license" file accompanying this file. This file is distributed
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the License for the specific language governing
 * permissions and limitations under the License.
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts';
import { Modal, Box, Button, SpaceBetween } from '@cloudscape-design/components';

/**
 * SessionExpirationHandler monitors token expiration and prompts user to re-authenticate
 * Preserves session state for seamless continuation after re-authentication
 */
export const SessionExpirationHandler: React.FC = () => {
  const { isAuthenticated, isTokenExpired, login } = useAuth();
  const [showExpirationModal, setShowExpirationModal] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;

    // Check token expiration every 30 seconds
    const interval = setInterval(() => {
      if (isTokenExpired()) {
        setShowExpirationModal(true);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [isAuthenticated, isTokenExpired]);

  const handleReAuthenticate = () => {
    // Save current location for redirect after login
    sessionStorage.setItem('redirectAfterLogin', window.location.pathname + window.location.search);
    
    // Close modal and redirect to login
    setShowExpirationModal(false);
    login();
  };

  return (
    <Modal
      visible={showExpirationModal}
      onDismiss={() => setShowExpirationModal(false)}
      header="Session Expired"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={() => setShowExpirationModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleReAuthenticate}>
              Re-authenticate
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <Box variant="p">
        Your session has expired. Please re-authenticate to continue using the application.
      </Box>
      <Box variant="p" color="text-status-info">
        Your current work will be preserved and you'll be returned to this page after logging in.
      </Box>
    </Modal>
  );
};
